/**
 * The one thing on screen when a session has died under the user's feet.
 *
 * A revoked or expired token does not take the app away — capture, the ledger
 * and every edit keep working exactly as they do offline, because Dexie is the
 * source of truth and the network is a background detail (INV-4, W-2). What is
 * lost is only *syncing*, so what the user needs is a way back, not a wall.
 *
 * **It stays silent until something is at stake.** With nothing pending, every
 * row is already on the server and a dead session costs precisely nothing; a
 * banner then would be a nag, and a nag is how people learn to dismiss the
 * message that matters. The moment anything is queued, it appears on every
 * route — including `/add`, which is the front door and the home-screen shortcut
 * target, and therefore the only screen some sessions ever visit.
 *
 * A full-bleed tinted band on a hairline, edge to edge, the whole thing tappable
 * (D-1). Plain copy: this is a control, and myth stays in the margins (D-7).
 */

import { cx } from "@/features/common/primitives";

export function SessionBand({
  pending,
  onSignIn,
}: {
  pending: number;
  onSignIn: () => void;
}): React.JSX.Element | null {
  if (pending <= 0) return null;

  return (
    <button
      type="button"
      onClick={onSignIn}
      className={cx(
        "flex min-h-[44px] w-full items-center justify-between gap-phi-n1",
        "border-b border-line bg-acc-soft px-phi-1 text-left",
        "active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-acc",
      )}
    >
      <span className="text-phi-n1 text-tx">
        Sync paused ·{" "}
        <span className="tnum font-semibold">
          {pending} {pending === 1 ? "change" : "changes"}
        </span>{" "}
        saved here only
      </span>
      <span className="shrink-0 text-phi-n1 font-semibold text-acc">Sign in again</span>
    </button>
  );
}
